import React from 'react';
import { Button, Typography } from '../../design-system';
import { SwHeightZone } from '../../@types/swHeight/types';
import { SwHeightPatchApi } from './useSwHeightSettingsPatch';
import { METHOD_HINT, SECTION_TITLES } from './swHeightSettings';
import SwHeightRockTypeGrid from './SwHeightRockTypeGrid';
import styles from './SwHeightRockTypeDock.module.css';

export interface SwHeightRockTypeDockProps {
  open: boolean;
  onClose: () => void;
  api: SwHeightPatchApi;
  zones: SwHeightZone[];
  disabled?: boolean;
}

/**
 * The per-rock-type parameters, docked full-width beneath the charts.
 *
 * Not a side drawer, unlike the other two groups. This one is a *table* — up
 * to seven columns, five of them editable — and a 480px sheet could not hold
 * it: `DataGrid` is `width: 100%; table-layout: fixed`, so a narrow container
 * does not scroll, it squeezes, and the unsized columns collapsed until their
 * `nowrap` contents overlapped. Width is the requirement, so the surface
 * spans the page.
 *
 * Pinned to the bottom of the scroll container rather than placed in the flow
 * below the charts: opening it must not scroll the tracks out of view, since
 * the entire reason to edit here is watching the Sw curves move. The charts
 * stay above it and keep scrolling behind.
 */
export const SwHeightRockTypeDock: React.FC<SwHeightRockTypeDockProps> = ({
  open,
  onClose,
  api,
  zones,
  disabled = false
}) => {
  if (!open) return null;

  return (
    <section
      className={styles.dock}
      aria-label={SECTION_TITLES['rock-types']}
      data-testid="sw-height-rock-type-dock"
    >
      <div className={styles.header}>
        <div className={styles.heading}>
          <Typography.Text weight="semibold">
            {SECTION_TITLES['rock-types']}
          </Typography.Text>
          {/* The method decides this table's columns, and the rail that
              carries the selector may be scrolled away. */}
          <Typography.Text size="sm">{METHOD_HINT[api.method]}</Typography.Text>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          data-testid="close-rock-type-dock"
        >
          Hide
        </Button>
      </div>

      <div className={styles.body} data-testid="per-rock-type-panel">
        <SwHeightRockTypeGrid
          zones={zones}
          method={api.method}
          overrides={api.overrides}
          onChange={api.patchOverrides}
          showHeader={false}
          disabled={disabled}
        />
      </div>
    </section>
  );
};

export default SwHeightRockTypeDock;
